// FILE: src/pages/StudentProfilePage.jsx
// My profile › Details. Students can change how we reach them (phone,
// address). Legal name and licence were checked for eligibility (MTO
// glossary: a valid Ontario G1 or G2, or no licence yet: owner decision D11)
// and identify the student in their course record, so only student support
// changes them (FIELD_NOT_EDITABLE).
// The address fields carry browser-autofill tokens, and the street field
// offers Google address suggestions when configured (StreetAddressField).
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfile, updateProfile } from "../features/profileSlice";
import ProfileShell from "../components/profile/ProfileShell";
import { DetailList, ErrorState, LoadingState, Section } from "../components/ui";
import { errorMessage, errorCode } from "../i18n/errors";
import { licenceClassLabel } from "../i18n/labels";
import { PROVINCES, normalisePostalCode, provinceCode } from "../components/student/provinces";
import StreetAddressField from "../components/student/StreetAddressField";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
}

function fromProfile(p) {
  return {
    phone: p?.phone || "",
    street: p?.address?.street || "",
    city: p?.address?.city || "",
    province: provinceCode(p?.address?.province) || "ON",
    postalCode: p?.address?.postalCode || ""
  };
}

function validate(v) {
  const e = {};
  const digits = v.phone.replace(/\D/g, "");
  if (v.phone && (digits.length < 10 || digits.length > 15 || /[^\d+\s\-().]/.test(v.phone))) {
    e.phone = "Enter a phone number with area code, e.g. 416 555 0100.";
  }
  if (!v.street.trim()) e.street = "Enter your street address.";
  if (!v.city.trim()) e.city = "Enter your city or town.";
  if (!v.province) e.province = "Choose your province or territory.";
  if (!normalisePostalCode(v.postalCode)) e.postalCode = "Enter a postal code like A1A 1A1.";
  return e;
}

export default function StudentProfilePage() {
  const dispatch = useDispatch();
  const { profile, loading, error } = useSelector((state) => state.profile);
  const [values, setValues] = useState(() => fromProfile(profile));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [file, setFile] = useState(null);
  const [fileState, setFileState] = useState(null); // "saving" | "saved" | { error }

  useEffect(() => {
    if (!profile && !loading && !error) dispatch(fetchProfile());
  }, [profile, loading, error, dispatch]);

  useEffect(() => {
    if (profile) setValues(fromProfile(profile));
  }, [profile]);

  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(fromProfile(profile)), [values, profile]);

  const setValue = (field, value) => {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((x) => ({ ...x, [field]: null }));
    setSaved(false);
  };
  const set = (field) => (e) => setValue(field, e.target.value);

  // A Google address suggestion was chosen: fill what it gave us.
  const fillAddress = (a) => {
    const filled = Object.fromEntries(["street", "city", "province", "postalCode"].filter((f) => a[f]).map((f) => [f, a[f]]));
    setValues((v) => ({ ...v, ...filled }));
    setErrors((x) => ({ ...x, ...Object.fromEntries(Object.keys(filled).map((f) => [f, null])) }));
    setSaved(false);
  };

  async function save(e) {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      await dispatch(
        updateProfile({
          phone: values.phone.trim(),
          address: {
            street: values.street.trim(),
            city: values.city.trim(),
            province: values.province,
            postalCode: normalisePostalCode(values.postalCode)
          }
        })
      ).unwrap();
      setSaved(true);
    } catch (err) {
      const problems = err?.problems || [];
      if (problems.length) {
        const map = {};
        for (const p of problems) {
          const key = String(p.field || "").replace(/^address\./, "");
          map[key] = "Check this field.";
        }
        setErrors(map);
      }
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  async function uploadLicence() {
    if (!file) return;
    setFileState("saving");
    try {
      const fd = new FormData();
      fd.append("driverLicense", file);
      await dispatch(updateProfile(fd)).unwrap();
      setFileState("saved");
      setFile(null);
    } catch (err) {
      setFileState({ error: err });
    }
  }

  if (!profile && error) {
    return (
      <ProfileShell title="My profile">
        <ErrorState error={error} onRetry={() => dispatch(fetchProfile())} />
      </ProfileShell>
    );
  }
  if (!profile) return <LoadingState variant="page" label="Loading your profile…" />;

  const legalName = [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ");

  return (
    <ProfileShell title="My profile" description="Your details, how we contact you and your enrolment.">
      <Stack spacing={3}>
        <Section
          id="identity"
          title="Identity and licence"
          description="These identify you in your course record. To correct them, contact student service support."
          action={
            <Button component={RouterLink} to="/student/support/new" variant="outlined" sx={{ flexShrink: 0 }}>
              Ask for a correction
            </Button>
          }
        >
          <DetailList
            items={[
              { label: "Legal name", value: legalName || "—" },
              { label: "Email (sign-in)", value: profile.email },
              { label: "Licence class", value: profile.licenseClass ? licenceClassLabel(profile.licenseClass) : "—" },
              { label: "Licence number", value: profile.licenseNumber || "—" }
            ]}
          />
        </Section>

        <Section id="enrolment" title="Enrolment" description="You have one year from registration to finish the course (MTO rule).">
          <DetailList
            items={[
              { label: "Registration number", value: profile.registrationNumber || "—" },
              { label: "Enrolment started", value: formatDate(profile.enrolmentStartedAt || profile.registrationDate) },
              { label: "Enrolment ends", value: formatDate(profile.enrolmentExpiresAt) },
              {
                label: "Days left",
                value: profile.daysLeft == null ? "—" : `${profile.daysLeft} ${profile.daysLeft === 1 ? "day" : "days"}`
              }
            ]}
          />
        </Section>

        <Section id="contact" title="Contact details" description="We use these only to reach you about your course.">
          <Box component="form" onSubmit={save} noValidate>
            {saved && (
              <Alert severity="success" sx={{ mb: 2 }} role="status" onClose={() => setSaved(false)}>
                Your contact details are saved.
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }} role="alert">
                {errorMessage(saveError)}
              </Alert>
            )}
            <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
              <TextField
                id="contact-phone"
                name="phone"
                label="Phone"
                type="tel"
                autoComplete="tel"
                inputProps={{ inputMode: "tel" }}
                value={values.phone}
                onChange={set("phone")}
                error={!!errors.phone}
                helperText={errors.phone || "Optional. Include the area code."}
              />
              <Box sx={{ display: { xs: "none", sm: "block" } }} />
              <StreetAddressField
                id="address-street"
                name="street"
                label="Street address"
                required
                value={values.street}
                onChange={(text) => setValue("street", text)}
                onAddressSelected={fillAddress}
                error={!!errors.street}
                helperText={errors.street || "For example: 123 Main St, Apt 4"}
                maxLength={120}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              />
              <TextField
                id="address-city"
                name="city"
                label="City or town"
                autoComplete="address-level2"
                required
                value={values.city}
                onChange={set("city")}
                error={!!errors.city}
                helperText={errors.city || " "}
                slotProps={{ htmlInput: { maxLength: 60 } }}
              />
              <FormControl required error={!!errors.province}>
                <InputLabel id="address-province-label" htmlFor="address-province">
                  Province or territory
                </InputLabel>
                {/* Native select: browser autofill can choose "ON" or "Ontario". */}
                <Select
                  native
                  labelId="address-province-label"
                  id="address-province"
                  label="Province or territory"
                  value={values.province}
                  onChange={set("province")}
                  inputProps={{
                    name: "province",
                    autoComplete: "address-level1",
                    "aria-describedby": errors.province ? "address-province-help" : undefined
                  }}
                >
                  {PROVINCES.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText id="address-province-help">{errors.province || " "}</FormHelperText>
              </FormControl>
              <TextField
                id="address-postal-code"
                name="postalCode"
                label="Postal code"
                autoComplete="postal-code"
                required
                slotProps={{ htmlInput: { autoCapitalize: "characters", spellCheck: false, maxLength: 7 } }}
                value={values.postalCode}
                onChange={(e) => setValue("postalCode", e.target.value.toUpperCase())}
                onBlur={() => {
                  const pc = normalisePostalCode(values.postalCode);
                  if (pc && pc !== values.postalCode) setValues((v) => ({ ...v, postalCode: pc }));
                }}
                error={!!errors.postalCode}
                helperText={errors.postalCode || "Like A1A 1A1"}
              />
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3, justifyContent: "flex-end" }}>
              <Button onClick={() => setValues(fromProfile(profile))} disabled={!dirty || saving}>
                Undo changes
              </Button>
              <Button type="submit" variant="contained" disabled={!dirty || saving}>
                {saving ? <CircularProgress size={22} color="inherit" aria-label="Saving" /> : "Save contact details"}
              </Button>
            </Stack>
          </Box>
        </Section>

        <Section
          id="licence-copy"
          title="Copy of your licence"
          description="If you have a G1 or G2 licence and we ask for a copy, upload a photo or scan of it here. You need at least a G1 before your in-car lessons. Only staff can see it."
        >
          {fileState === "saved" && (
            <Alert severity="success" sx={{ mb: 2 }} role="status">
              Thanks, your licence copy was uploaded.
            </Alert>
          )}
          {fileState?.error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {errorCode(fileState.error) ? errorMessage(fileState.error) : "We couldn't upload that file. Try a JPG, PNG or PDF under 2 MB."}
            </Alert>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />}>
              Choose a file
              <input
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setFileState(null);
                }}
              />
            </Button>
            <Typography sx={{ color: "text.secondary", flex: 1, overflowWrap: "anywhere" }} aria-live="polite">
              {file ? `Selected: ${file.name}` : "JPG, PNG or PDF, up to 2 MB."}
            </Typography>
            <Button variant="contained" onClick={uploadLicence} disabled={!file || fileState === "saving"}>
              {fileState === "saving" ? <CircularProgress size={22} color="inherit" aria-label="Uploading" /> : "Upload"}
            </Button>
          </Stack>
        </Section>

        <Typography sx={{ color: "text.secondary" }}>
          Looking for your password or security questions? They're under{" "}
          <Link component={RouterLink} to="/student/profile/security">
            Security
          </Link>
          .
        </Typography>
      </Stack>
    </ProfileShell>
  );
}
